import { c as _c } from "react/compiler-runtime";
import { IcoFileText24, IcoInfo16, IcoTrash16, IcoWarning16 } from '@onefootprint/icons';
import { Button, Divider, IconButton } from '@onefootprint/ui';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { InvestorProfileFormData } from '../types';
type UploadComplianceLetterProps = {
  isRequired?: boolean;
};
const FILE_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB

export const UploadComplianceLetter = t0 => {
  const $ = _c(7);
  const {
    isRequired
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectInvestorProfile.declarations.docUpload"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const {
    control
  } = useFormContext();
  let t2;
  if ($[1] !== isRequired || $[2] !== t) {
    t2 = {
      validate: value => {
        if (isRequired && (!value || value.length === 0)) {
          return t(_temp);
        }
        return true;
      }
    };
    $[1] = isRequired;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== control || $[5] !== t2) {
    t3 = <Controller name="declarationFiles" control={control} rules={t2} render={_temp2} />;
    $[4] = control;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
type UploadComplianceLetterContentProps = {
  files: File[];
  onChange: (files: File[]) => void;
  hasRequiredError: boolean;
};
const UploadComplianceLetterContent = t0 => {
  const $ = _c(47);
  const {
    files,
    onChange,
    hasRequiredError
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectInvestorProfile.declarations.docUpload"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const [hasSizeError, setHasSizeError] = useState(false);
  const inputRef = useRef(null);
  let t2;
  if ($[1] !== onChange) {
    t2 = event => {
      if (!event.target.files?.length) {
        return;
      }
      const uploads = event.target.files;
      const isOverLimit = Array.from(uploads).some(_temp3);
      if (isOverLimit) {
        setHasSizeError(true);
      } else {
        setHasSizeError(false);
        onChange([uploads[0]]);
      }
    };
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleFileChange = t2;
  let t3;
  if ($[3] !== onChange) {
    t3 = () => {
      onChange([]);
      setHasSizeError(false);
    };
    $[3] = onChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleRemoveFile = t3;
  let t4;
  if ($[5] !== files.length) {
    t4 = () => {
      if (files.length === 0 && inputRef.current) {
        inputRef.current.value = "";
      }
    };
    $[5] = files.length;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== files) {
    t5 = [files];
    $[7] = files;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Divider />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IcoFileText24 color="primary" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t) {
    t8 = t(_temp4);
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <div className="flex items-center gap-1">{t7}<span className="text-label-2 text-secondary">{t8}</span></div>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => inputRef.current?.click();
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t) {
    t11 = t(_temp5);
    $[16] = t;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <Button fullWidth={true} onClick={t10} variant="secondary" size="large">{t11}</Button>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== handleFileChange) {
    t13 = <input ref={inputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />;
    $[20] = handleFileChange;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== files || $[23] !== handleRemoveFile || $[24] !== t) {
    t14 = files.length > 0 && <><Divider /><div className="flex flex-col gap-2">{files.map(file_0 => <div key={file_0.name} className="flex items-center justify-between min-h-8"><span className="text-body-2 text-primary">{file_0.name}</span><IconButton aria-label={t(_temp6)} onClick={handleRemoveFile} icon={IcoTrash16} /></div>)}</div></>;
    $[22] = files;
    $[23] = handleRemoveFile;
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = <div className="bg-secondary p-4 rounded-md flex flex-col gap-4">{t12}{t13}{t14}</div>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== hasSizeError || $[31] !== t) {
    t16 = <div className="flex justify-center">{hasSizeError ? <span className="text-caption-3 text-error">{t(_temp7)}</span> : <span className="text-caption-3 text-quaternary flex items-center gap-1"><IcoInfo16 color="quaternary" />{t(_temp8)}</span>}</div>;
    $[30] = hasSizeError;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== hasRequiredError || $[34] !== t) {
    t17 = hasRequiredError && <div className="flex items-center gap-1"><IcoWarning16 color="error" /><span className="text-body-2 text-error">{t(_temp9)}</span></div>;
    $[33] = hasRequiredError;
    $[34] = t;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t(_temp10);
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <p className="text-caption-4 text-tertiary">{t18}</p>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Divider />;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t15 || $[42] !== t16 || $[43] !== t17 || $[44] !== t19 || $[45] !== t9) {
    t21 = <div className="flex flex-col gap-4">{t6}{t9}{t15}{t16}{t17}{t19}{t20}</div>;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
    $[44] = t19;
    $[45] = t9;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
export default UploadComplianceLetter;
function _temp($) {
  return $.required;
}
function _temp2(t0) {
  const {
    field: t1,
    fieldState: t2
  } = t0;
  const {
    value: value_0,
    onChange
  } = t1;
  const {
    error
  } = t2;
  return <UploadComplianceLetterContent files={value_0 ?? []} onChange={onChange} hasRequiredError={!!error} />;
}
function _temp3(file) {
  return file.size > FILE_SIZE_LIMIT;
}
function _temp4($) {
  return $.label;
}
function _temp5($_0) {
  return $_0.cta;
}
function _temp6($_1) {
  return $_1.removeAriaLabel;
}
function _temp7($_2) {
  return $_2.sizeLimitError;
}
function _temp8($_3) {
  return $_3.hint;
}
function _temp9($_4) {
  return $_4.required;
}
function _temp10($_5) {
  return $_5.disclaimer;
}